<template>
  <v-layout v-if="!$apollo.loading" column class="wrapper">
    <v-flex class="title-screen" xs12>
      <span>Floor plan overview</span>
    </v-flex>
    <v-select
      v-if="eventTemplateId"
      v-model="selectedEventId"
      :items="performanceDates"
      label="Performance"
      item-text="from"
      item-value="id"
      box
      style="max-width: 1000px"
    />
    <spinner v-if="!showFloorPlan" />
    <div v-else-if="!venue">This event does not have a floor plan.</div>
    <div v-else class="floor-plan-wrapper">
      <div class="order-items-container">
        <div
          v-for="orderItem in orderItems"
          :key="orderItem.id"
          class="order-item-container"
          :style="{ borderColor: orderItem.selected ? '#D9D9D9' : '#fff' }"
          @click="handleOrderItemClick(orderItem)"
        >
          <div class="color" :style="{ backgroundColor: orderItem.color }" />
          <div>
            <span class="base-text">{{ orderItem.name }}: </span>
            <span class="base-text bold-text">{{ orderItem.ticketsNumber }}</span>
          </div>
        </div>
      </div>
      <div class="floor-plan-container">
        <div class="object-container">
          <spinner v-if="coloringInProgress" />
          <object
            v-show="!coloringInProgress"
            id="svgfile"
            :data="venue.imageUrl"
            type="image/svg+xml"
            style="width: 100%"
            @load="changeSeatColors"
          />
        </div>
        <div class="total-label">
          <span class="base-text">Free </span>
          <span class="base-text bold-text">
            {{ calculateTotalAvailableSeats() }}/{{ audienceCapacity }}
          </span>
        </div>
        <div class="zoom-controls">
          <v-icon
            :size="43"
            :color="zoomLevel > 2.5 ? '#D9D9D9' : '#333'"
            style="user-select: none"
            @click="zoomIn"
          >
            add_circle
          </v-icon>
          <v-icon
            :size="43"
            :color="zoomLevel < 0.2 ? '#D9D9D9' : '#333'"
            style="user-select: none"
            @click="zoomOut"
          >
            remove_circle
          </v-icon>
        </div>
      </div>
    </div>
  </v-layout>
</template>

<script>
import Spinner from '../shared/spinner.vue'
import {
  GET_EVENTS_BY_EVENT_TEMPLATE_ID,
  GET_FLOOR_PLAN_AVAILABILITY_OVERVIEW_DATA,
} from '@/graphql/event.graphql'
import moment from 'moment/moment'

export default {
  name: 'floor-plan-overview',
  components: {
    Spinner,
  },
  props: {
    eventId: String,
    eventTemplateId: String,
    organizationSlug: String,
  },
  data() {
    return {
      selectedEventId: null,
      performanceDates: [],
      showFloorPlan: false,
      venue: null,
      orderItems: [],
      availableSeats: [],
      disabledSeats: [],
      zoomLevel: 1,
      lastPinchDistance: null,
      coloringInProgress: true,
      audienceCapacity: 0,
    }
  },
  watch: {
    async selectedEventId(val) {
      if (!val) return
      this.showFloorPlan = false

      const { data } = await this.$apollo.query({
        query: GET_FLOOR_PLAN_AVAILABILITY_OVERVIEW_DATA,
        variables: { eventId: this.selectedEventId },
      })
      if (!data.getFloorPlanAvailabilityOverviewData) {
        return (this.showFloorPlan = true)
      }
      const { venue, soldOrderItems, availableSeats, disabledSeats, audienceCapacity } =
        data.getFloorPlanAvailabilityOverviewData

      this.venue = venue
      this.orderItems = soldOrderItems.map(item => {
        return {
          ...item,
          selected: false,
        }
      })
      this.availableSeats = availableSeats
      this.disabledSeats = disabledSeats
      this.audienceCapacity = audienceCapacity

      this.coloringInProgress = false
      this.showFloorPlan = true
    },
  },
  created() {
    if (this.eventTemplateId) {
      this.fetchPerformances()
    } else {
      this.selectedEventId = this.eventId
    }
  },
  methods: {
    changeSeatColors() {
      const svgDoc = document.getElementById('svgfile').contentDocument

      for (const availableSeatId of this.availableSeats) {
        const circle = svgDoc.getElementById(availableSeatId)

        if (circle) {
          circle.style.fill = '#979797'
        }
      }

      for (const orderItem of this.orderItems) {
        const soldSeatsIds = orderItem.seats

        for (const seatId of soldSeatsIds) {
          const circle = svgDoc.getElementById(seatId)

          if (circle) {
            if (this.availableSeats.includes(seatId)) {
              circle.style.stroke = orderItem.selected ? orderItem.color : '#F5F5F5'
              circle.style.strokeWidth = 5
            } else {
              circle.style.fill = orderItem.selected ? orderItem.color : '#F5F5F5'
            }
          }
        }
      }

      for (const disabledSeatId of this.disabledSeats) {
        const circle = svgDoc.getElementById(disabledSeatId)

        if (circle) {
          circle.style.fill = '#D9D9D9'
        }
      }

      this.coloringInProgress = false

      const floorPlanContainer = document.querySelector('.floor-plan-container')
      floorPlanContainer.addEventListener('touchstart', this.handleTouchStart, { passive: false })
      floorPlanContainer.addEventListener('touchmove', this.handleTouchMove, { passive: false })
      floorPlanContainer.addEventListener('touchend', this.handleTouchEnd)
    },
    handleOrderItemClick(orderItem) {
      orderItem.selected = !orderItem.selected
      this.changeSeatColors()
    },
    calculateTotalAvailableSeats() {
      return (
        this.audienceCapacity - this.orderItems.reduce((acc, curr) => acc + curr.ticketsNumber, 0)
      )
    },
    zoomIn() {
      if (this.zoomLevel > 2.5) return

      this.zoomLevel += 0.1
      this.applyZoom()
    },
    zoomOut() {
      if (this.zoomLevel < 0.2) return

      this.zoomLevel -= 0.1
      this.applyZoom()
    },

    applyZoom() {
      const svg = document.getElementById('svgfile')
      svg.style.transform = `scale(${this.zoomLevel})`
    },
    handleTouchStart(event) {
      event.preventDefault()

      if (event.touches.length >= 2) {
        this.lastPinchDistance = Math.hypot(
          event.touches[0].pageX - event.touches[1].pageX,
          event.touches[0].pageY - event.touches[1].pageY,
        )
      }
    },
    handleTouchMove(event) {
      event.preventDefault()

      if (this.lastPinchDistance !== null && event.touches.length >= 2) {
        const newPinchDistance = Math.hypot(
          event.touches[0].pageX - event.touches[1].pageX,
          event.touches[0].pageY - event.touches[1].pageY,
        )

        const delta = newPinchDistance - this.lastPinchDistance
        const sensitivity = 0.01

        if (delta > 0) {
          this.zoomIn(sensitivity * delta)
        } else if (delta < 0) {
          this.zoomOut(-sensitivity * delta)
        }

        this.lastPinchDistance = newPinchDistance
      }
    },

    handleTouchEnd() {
      this.lastPinchDistance = null
    },

    async fetchPerformances() {
      const {
        data: { getEventsByEventTemplateId },
      } = await this.$apollo.query({
        query: GET_EVENTS_BY_EVENT_TEMPLATE_ID,
        variables: {
          organizationSlug: this.organizationSlug,
          eventTemplateId: this.eventTemplateId,
        },
      })

      this.performanceDates = getEventsByEventTemplateId.map(p => {
        return { ...p, from: moment(p.from).format('DD.MM.YYYY u HH:mm') }
      })
      this.selectedEventId = this.performanceDates[0].id || ''
    },
  },
}
</script>

<style scoped>
.wrapper {
  max-width: 1033px;
}

@media screen and (max-width: 800px) {
  .wrapper {
    margin-top: 1.5rem;
  }
}

.floor-plan-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 0.75rem;
  border: 1px solid #d9d9d9;
}

.order-items-container {
  display: flex;
  padding: 1rem 1.5rem;
  align-items: center;
  gap: 1.5rem;
  align-self: stretch;
  border-bottom: 1px solid #d9d9d9;
  overflow: hidden;
  flex-wrap: wrap;
}

.order-item-container {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  gap: 0.5rem;
  border-radius: 0.25rem;
  border: 1px solid #fff;
}

.order-item-container:hover {
  cursor: pointer;
}

.color {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 10000px;
}

.base-text {
  color: #666;
  font-family: 'Source Sans Pro';
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.bold-text {
  font-weight: 700;
}

.floor-plan-container {
  padding: 3rem;
  position: relative;
  overflow: hidden;
  width: 100%;
}

.object-container {
  overflow: scroll;

  -ms-overflow-style: none;
  scrollbar-width: none;
}

.object-container::-webkit-scrollbar {
  display: none;
}

.total-label {
  position: absolute;
  left: 1.5rem;
  bottom: 1.5rem;
}

.zoom-controls {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1rem;
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
}
</style>
